import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../common/types';
import { AddArtistModal } from './AddArtistModal';
import { DemoArtistTag } from './DemoArtistTag';
import { demoArtistMessages as messages } from './demoArtistMessages';
import { firstOwnAccountId, isDemoAccount } from './demoAccounts';
import { shouldShowDemoNotice } from './demoNoticeRule';
import { useArtistSwitcher } from './useArtistSwitcher';
import { useDemoNoticeSession } from './useDemoNoticeSession';
import styles from './DemoArtistNotice.module.scss';

export interface DemoArtistNoticeProps {
  /** Classes on the wrapper; the shell passes its page padding. */
  className?: string;
}

// The key: react-intl returns the parts as a list, so each element in it needs one.
const bold = (chunks: React.ReactNode[]) => <b key="b">{chunks}</b>;

/**
 * design: music24-demo-artist a2/a4. One slim line on top of every page while the read-only demo
 * artist (LaziiBee) is active. "Switch to my artist" selects the first artist that is not the
 * demo, or opens "Add artist" when there is none. The close button hides it for the session, and
 * `?demo-clean=1` hides it for screenshots and videos (a6).
 */
const DemoArtistNoticeBodyComponent: React.FC<DemoArtistNoticeProps> = ({ className }) => {
  const intl = useIntl();
  const { search } = useLocation();
  const switcher = useArtistSwitcher();
  const { accounts, active, onSelect, openAdd } = switcher;
  const { isDismissed, isCleanForSession, dismiss } = useDemoNoticeSession(search);
  const ownAccountId = useMemo(() => firstOwnAccountId(accounts), [accounts]);

  const switchToOwn = useCallback(() => {
    if (ownAccountId === null) {
      openAdd();
      return;
    }
    onSelect(ownAccountId);
  }, [onSelect, openAdd, ownAccountId]);

  const isVisible = shouldShowDemoNotice({
    isDemoActive: isDemoAccount(active),
    search,
    isCleanForSession,
    isDismissed,
  });
  if (!isVisible || active === undefined) {
    return null;
  }

  return (
    <div className={className}>
      <div
        role="status"
        className={clsx(styles.notice, 'd-flex flex-wrap flex-md-nowrap align-items-center gap-2 mb-3')}
        data-testid="demo-artist-notice"
      >
        <DemoArtistTag />
        <span className={styles.text}>{intl.formatMessage(messages.notice, { name: active.name ?? '', b: bold })}</span>
        <button type="button" className={clsx(styles.action, 'order-last order-md-0')} onClick={switchToOwn}>
          {intl.formatMessage(messages.switchToOwn)}
        </button>
        <button
          type="button"
          className={styles.dismiss}
          onClick={dismiss}
          aria-label={intl.formatMessage(messages.dismiss)}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>
      <AddArtistModal isOpen={switcher.isAddOpen} onClose={switcher.closeAdd} />
    </div>
  );
};

export const DemoArtistNoticeBody = TypedMemo(DemoArtistNoticeBodyComponent);
